import {
  all,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'
import {
  FETCH_CLINIQUES,
  FETCH_OPTIONS,
  FETCH_REGIONS,
  FETCH_TREATMENTS,
  SAVE_FORM,
  UPDATE_CURRENT_CLINIQUE,
  UPDATE_CURRENT_PERSON_TYPE,
  UPDATE_CURRENT_REGION,
  UPDATE_CURRENT_STEP,
  UPDATE_CURRENT_TREATMENT,
  updateActiveRegions,
  updateCliniques,
  updateCurrentStep,
  updateFormIsSubmitting,
  updateOptions,
  updateRegions,
  updateTreatments,
} from 'components/core/state/actions'
import wordpressApiService from 'components/core/services/wordpress-api'
import { IApplicationState } from 'components/store/types'
import {
  getCliniques,
  getHideUrlStatus,
  getLocale,
  getQueryParams,
  getRegions,
} from 'components/core/state/selectors'
import {
  IClinique,
  IRegion,
  ISaveFormAction,
  ISaveFormPayload,
  ISetSearchParams,
  IUpdateCurrentCliniqueAction,
  IUpdateCurrentPersonTypeAction,
  IUpdateCurrentRegionAction,
  IUpdateCurrentStepAction,
  IUpdateCurrentTreatmentAction,
} from 'components/core/types'

function* getActiveRegions() {
  const cliniques = yield select((state: IApplicationState) => getCliniques(state))
  const regions = yield select((state: IApplicationState) => getRegions(state))
  const cliniqueRegions = cliniques.map((clinique: IClinique) => clinique.region)
  const activeRegionIds = Array.from(new Set([].concat(...cliniqueRegions)))
  return regions.filter((region: IRegion) => activeRegionIds.includes(region.id))
    .sort(({ order: a }: IRegion, { order: b }: IRegion) => a > b)
}

function* fetchCliniques() {
  const locale = yield select((state: IApplicationState) => getLocale(state))
  const cliniques = yield wordpressApiService.getCliniques(locale)
  yield put(updateCliniques(cliniques))
  const activeRegions = yield getActiveRegions()
  yield put(updateActiveRegions(activeRegions))
}

function* fetchOptions() {
  const locale = yield select((state: IApplicationState) => getLocale(state))
  const options = yield wordpressApiService.getOptions(locale)
  yield put(updateOptions(options))
}

function* fetchRegions() {
  const locale = yield select((state: IApplicationState) => getLocale(state))
  const regions = yield wordpressApiService.getRegions(locale)
  yield put(updateRegions(regions))
  const activeRegions = yield getActiveRegions()
  yield put(updateActiveRegions(activeRegions))
}

function* fetchTreatments() {
  const locale = yield select((state: IApplicationState) => getLocale(state))
  const treatments = yield wordpressApiService.getTreatments(locale)
  yield put(updateTreatments(treatments))
}

function* saveForm(action: ISaveFormAction) {
  try {
    const { data, setSearchParams } = action.payload as ISaveFormPayload
    yield put(updateFormIsSubmitting(true))
    yield wordpressApiService.saveAppointmentForm(3, data)
    yield put(updateCurrentStep(4, setSearchParams))
    yield put(updateFormIsSubmitting(false))
  } catch {
    yield put(updateFormIsSubmitting(false))
  }
}

function* updateSearchParams(setSearchParams: ISetSearchParams, values: { [key: string]: string }) {
  const hideUrl = yield select((state: IApplicationState) => getHideUrlStatus(state))
  if (!hideUrl) {
    const queryParams = yield select((state: IApplicationState) => getQueryParams(state))
    setSearchParams({ ...queryParams, ...values })
  }
}

function* updateClinique(action: IUpdateCurrentCliniqueAction) {
  const { cliniqueId, setSearchParams } = action.payload
  yield updateSearchParams(setSearchParams, { rafClinique: cliniqueId.toString() })
}

function* updatePersonType(action: IUpdateCurrentPersonTypeAction) {
  const { personTypeId, setSearchParams } = action.payload
  yield updateSearchParams(setSearchParams, { rafPersonType: personTypeId.toString() })
}

function* updateRegion(action: IUpdateCurrentRegionAction) {
  const { regionId, setSearchParams } = action.payload
  yield updateSearchParams(setSearchParams, { rafRegion: regionId.toString() })
}

function* updateStep(action: IUpdateCurrentStepAction) {
  const { step, setSearchParams } = action.payload
  if (step > 4 || step < 0) {
    return
  }
  yield updateSearchParams(setSearchParams, { rafStep: step.toString() })
}

function* updateTreatment(action: IUpdateCurrentTreatmentAction) {
  const { treatmentId, setSearchParams } = action.payload
  yield updateSearchParams(setSearchParams, { rafTreatment: treatmentId.toString() })
}

const cliniquesSaga: () => void = function* cliniquesSaga() {
  all([
    yield takeLatest(FETCH_CLINIQUES, fetchCliniques),
    yield takeLatest(FETCH_OPTIONS, fetchOptions),
    yield takeLatest(FETCH_REGIONS, fetchRegions),
    yield takeLatest(FETCH_TREATMENTS, fetchTreatments),
    yield takeLatest(SAVE_FORM, saveForm),
    yield takeLatest(UPDATE_CURRENT_CLINIQUE, updateClinique),
    yield takeLatest(UPDATE_CURRENT_PERSON_TYPE, updatePersonType),
    yield takeLatest(UPDATE_CURRENT_REGION, updateRegion),
    yield takeLatest(UPDATE_CURRENT_STEP, updateStep),
    yield takeLatest(UPDATE_CURRENT_TREATMENT, updateTreatment),
  ])
}
export default cliniquesSaga
